import { RefObject, useCallback, useEffect, useState } from 'react';

interface ClickAwayProps {
  ref: RefObject<HTMLElement | null>;
  onClickAway?: () => void;
}

/**
 * **useClickAway**
 *
 * Detects when a user clicks outside a specified DOM element.
 * Triggers the optional `onClickAway` callback and returns a `clickedAway` boolean
 * indicating if an outside click has occurred since mount.
 *
 * This is useful for dismissing popups, dropdowns, modals, etc.
 *
 * @param ref - React ref to the element to watch for outside clicks
 * @param onClickAway - Optional callback invoked on the first outside click
 * @returns `true` if a click outside has occurred; otherwise `false`
 */
export function useClickAway({ ref, onClickAway }: Readonly<ClickAwayProps>) {
  const [clickedAway, setClickedAway] = useState(false);

  // Detect if click outside of the element
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      // No ref
      if (!ref?.current) return;

      // Click is inside -> ignore
      if (ref.current.contains(event.target as Node)) return;

      // Click is outside -> trigger
      onClickAway?.();
      // Set to true
      setClickedAway(true);
    },
    [ref, onClickAway],
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [handleClickOutside]);

  return clickedAway;
}
